import { useEffect } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

/**
 * Reusable FAQ accordion with FAQPage JSON-LD schema injection.
 * Used on /faq (full list) and /services (condensed subset).
 */
export default function FAQAccordion({ items, schemaId = "faq-jsonld", injectSchema = true }) {
  useEffect(() => {
    if (!injectSchema || !items?.length) return;
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = schemaId;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [items, schemaId, injectSchema]);

  if (!items?.length) return null;

  return (
    <Accordion type="single" collapsible className="w-full divide-y divide-[#27272A] border border-[#27272A] bg-[#0F0F0F]" data-testid="faq-accordion">
      {items.map((f, i) => (
        <AccordionItem key={i} value={`item-${i}`} className="border-0">
          <AccordionTrigger className="px-5 md:px-6 py-5 text-left font-display uppercase tracking-tight text-base md:text-lg hover:text-[#FF331F] hover:no-underline data-[state=open]:text-[#FF331F]" data-testid={`faq-question-${i}`}>
            {f.q}
          </AccordionTrigger>
          <AccordionContent className="px-5 md:px-6 pb-5 text-zinc-300 leading-relaxed text-[15px]" data-testid={`faq-answer-${i}`}>
            {f.a}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
